import { Button, Checkbox, Modal } from '@skupreme/shared/ui';
import { useEffect, useState } from 'react';
import {
  checkState,
  freeStates,
  isSelected,
  isTaken,
  toggleStates,
  type RegionSelection,
  type RegionTree,
} from '../../lib/shipping-template';

type RegionSelectModalProps = {
  isOpen: boolean;
  tree: RegionTree;
  /** States another row of the same method already covers. */
  taken: RegionSelection;
  initial: RegionSelection;
  onClose: () => void;
  onConfirm: (selection: RegionSelection) => void;
};

/** Regions, sub-regions and states one row of a shipping method covers. */
export function RegionSelectModal({
  isOpen,
  tree,
  taken,
  initial,
  onClose,
  onConfirm,
}: RegionSelectModalProps) {
  const [selection, setSelection] = useState<RegionSelection>(initial);

  useEffect(() => {
    if (isOpen) setSelection(initial);
  }, [isOpen, initial]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      title="Select regions"
      showCloseButton
      size="xl"
      bodyClassName="sk:max-h-[70vh] sk:overflow-y-auto"
      footer={
        <>
          <Button variant="ghost" onPress={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => onConfirm(selection)}>
            Confirm
          </Button>
        </>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-4">
        {tree.map(region => {
          const regionStates = freeStates(tree, taken, region.regionCode);
          return (
            <section
              key={region.regionCode}
              className="sk:flex sk:flex-col sk:gap-1"
            >
              <Checkbox
                {...checkState(selection, regionStates)}
                isDisabled={regionStates.length === 0}
                onChange={on =>
                  setSelection(current =>
                    toggleStates(current, regionStates, on),
                  )
                }
              >
                <span className="sk:font-semibold">{region.regionName}</span>
              </Checkbox>
              {region.subRegions.map(sub => {
                const subStates = freeStates(
                  tree,
                  taken,
                  region.regionCode,
                  sub.subRegionCode,
                );
                return (
                  <div
                    key={sub.subRegionCode}
                    className="sk:grid sk:grid-cols-[10rem_1fr] sk:gap-3 sk:border-b sk:border-rule sk:py-2 sk:pl-6"
                  >
                    <Checkbox
                      {...checkState(selection, subStates)}
                      isDisabled={subStates.length === 0}
                      className="sk:self-start"
                      onChange={on =>
                        setSelection(current =>
                          toggleStates(current, subStates, on),
                        )
                      }
                    >
                      {sub.subRegionName}
                    </Checkbox>
                    <div className="sk:flex sk:flex-wrap sk:gap-x-4 sk:gap-y-2">
                      {sub.states.map(state => {
                        const entry = {
                          regionCode: region.regionCode,
                          subRegionCode: sub.subRegionCode,
                          ...state,
                        };
                        return (
                          <Checkbox
                            key={state.stateCode}
                            isSelected={isSelected(selection, entry)}
                            isDisabled={isTaken(taken, entry)}
                            onChange={on =>
                              setSelection(current =>
                                toggleStates(current, [entry], on),
                              )
                            }
                          >
                            {state.stateName}
                          </Checkbox>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}
